import {cookieManager} from '@skiwo/sk-basic-components';
import {cookieDomain} from '@/modules/constants';
import jwtDecode from 'jwt-decode';

const isBrowser = new Function('try {return this===window;}catch(e){ return false;}');

export default ({$fetcher: fetchData}) => {
  return {
    namespaced: true,
    state: {
      godmode: false,
      support: false,
      loggedIn: false,
      resetPasswordEmail: '',
      punchOutRedirectUrl: '',
      // 2FA state
      twoFactorRequired: false,
      twoFactorType: '',
      authSessionId: '',
      authSessionExpiresAt: '',
      maskedDestination: '',
      challengeToken: '',
      challengeExpiresAt: '',
      challengeSequence: '',
      nextChallengeAvailableAt: ''
    },
    getters: {
      isGodmode(state) {
        return state.godmode;
      },
      punchOutRedirectUrl(state) {
        return state.punchOutRedirectUrl;
      },
      // 2FA getters
      isTwoFactorRequired(state) {
        return state.twoFactorRequired;
      },
      getTwoFactorType(state) {
        return state.twoFactorType;
      },
      getAuthSessionId(state) {
        return state.authSessionId;
      },
      isAuthSessionExpired(state) {
        if (!state.authSessionExpiresAt) return true;
        return new Date(state.authSessionExpiresAt) < new Date();
      },
      getChallengeToken(state) {
        return state.challengeToken;
      },
      isChallengeExpired(state) {
        if (!state.challengeExpiresAt) return true;
        return new Date(state.challengeExpiresAt) < new Date();
      },
      getNextChallengeAvailableAt(state) {
        return state.nextChallengeAvailableAt;
      },
      getMaskedDestination(state) {
        return state.maskedDestination;
      },
      getChallengeSequence(state) {
        return state.challengeSequence;
      }
    },
    actions: {
      validateToken({commit}) {
        return new Promise((resolve, reject) => {
          fetchData.checkJwtToken().then(() => {
            resolve();
          }).catch((error) => {
            if (error?.status == 422) {
              commit('logOut');
              commit('GlobalStore/remove404Page', '', {root: true});
              resolve('LOGOUT');
            }
            reject(error.data);
          });
        });
      },
      getGodModeToken(context, params) {
        return new Promise((resolve, reject) => {
          fetchData.getGodModeToken(params).then((data) => {
            resolve(data);
          }).catch((response) => {
            reject(response);
          });
        });
      },
      checkSupportMode({commit}) {
        return new Promise((resolve, reject) => {
          fetchData.checkSupportMode().then((response) => {
            if (response?.code == 401) {
              commit('logOut');
              commit('GlobalStore/remove404Page', '', {root: true});
              resolve('LOGOUT');
              return;
            }
            commit('setSupportMode');
            resolve();
          }).catch((error) => {
            reject(error.data);
          });
        });
      },
      logOut({commit}) {
        return new Promise((resolve, reject) => {
          fetchData.signOut().then(() => {
            commit('logOut');
            resolve();
          }).catch((error) => {
            reject(error);
          });
        });
      },
      authorize({rootState, dispatch, commit}, {form, type, rememberMe}) {
        return new Promise((resolve, reject) => {
          fetchData.sendAuthData(form, type).then((data) => {
            // Check if 2FA is required
            if (data.twoFactorRequired) {
              commit('setTwoFactorData', data);
              resolve(data);
              return;
            }

            // Normal login flow
            dispatch('logIn', {token: data.token, uid: data.identity.uid, rememberMe: rememberMe});
            if (isBrowser()) {
              if (cookieManager.getCookie('userInfo')) {
                const userObj = JSON.parse(cookieManager.getCookie('userInfo'));
                rootState.UserInfoStore.additionalUserInfo.favouriteInterLang = userObj.favouriteInterLang;
                rootState.UserInfoStore.additionalUserInfo.onlineInterLang = userObj.onlineInterLang;
              }
            }
            resolve(data);
          }).catch((data) => {
            reject(data);
          });
        });
      },
      logIn({commit}, {token, uid, rememberMe, punchOutRedirectUrl}) {
        const decoded = jwtDecode(token);
        commit('UserInfoStore/setAdminInfo', decoded.godmode ? decoded : {}, {root: true});
        commit('logIn', {token, uid, rememberMe, punchOutRedirectUrl});
      },
      // 2FA actions
      sendTwoFactorCode({commit, state}) {
        return new Promise((resolve, reject) => {
          fetchData.sendTwoFactorCode({authSessionId: state.authSessionId}).then((data) => {
            commit('setChallengeData', data);
            resolve(data);
          }).catch((error) => {
            reject(error);
          });
        });
      },
      verifyTwoFactorCode({commit, state}, verificationToken) {
        return new Promise((resolve, reject) => {
          fetchData.verifyTwoFactorCode({
            // challengeToken: state.challengeToken,
            authSessionId: state.authSessionId,
            verificationToken: verificationToken
          }).then((data) => {
            // Clear 2FA data and proceed with normal login
            commit('clearTwoFactorData');
            resolve(data);
          }).catch((error) => {
            reject(error);
          });
        });
      },
      loadTwoFactorDataFromStorage({commit}) {
        if (isBrowser()) {
          const twoFactorData = localStorage.getItem('twoFactorData');
          const challengeData = localStorage.getItem('challengeData');

          if (twoFactorData) {
            const parsed = JSON.parse(twoFactorData);
            commit('setTwoFactorData', parsed);
          }

          if (challengeData) {
            const parsed = JSON.parse(challengeData);
            commit('setChallengeData', parsed);
          }
        }
      },
      clearTwoFactorDataFromStorage({commit}) {
        if (isBrowser()) {
          localStorage.removeItem('twoFactorData');
          localStorage.removeItem('challengeData');
        }
        commit('clearTwoFactorData');
      }
    },
    mutations: {
      logOut(state) {
        state.loggedIn = false;
        // Clear 2FA data on logout
        state.twoFactorRequired = false;
        state.twoFactorType = '';
        state.authSessionId = '';
        state.authSessionExpiresAt = '';
        state.maskedDestination = '';
        state.challengeToken = '';
        state.challengeExpiresAt = '';
        state.nextChallengeAvailableAt = '';
        if (isBrowser()) {
          const authTokenName = `${process.env.NODE_ENV}_authToken`;
          if (cookieManager.getCookie(authTokenName)) {
            const cookieParams = {path: '/', expires: -2};
            const domain = cookieDomain(window.location.hostname);
            if (domain) cookieParams.domain = domain;
            cookieManager.setCookie(authTokenName, '', cookieParams);
          }
          cookieManager.setCookie(authTokenName, '', {path: '/', expires: -2});
          cookieManager.setCookie('uID', '', {path: '/'});
          cookieManager.setCookie('userInfo', '', {path: '/'});
          cookieManager.setCookie('featureFlags', '', {path: '/'});
          cookieManager.setCookie('godmode', '', {path: '/'});
          cookieManager.setCookie('punch_out_redirect_url', '', {path: '/'});
          cookieManager.setCookie('lastJobCreatedAt', '', {path: '/'});
          cookieManager.setCookie('assignmentHelperEnabled', true, {path: '/'});
          if (cookieManager.getCookie('user_id_hash')) {
            cookieManager.setCookie('user_id_hash', '', {path: '/', expires: 2592000});
          }
          if (localStorage) {
            localStorage.removeItem('uID');
            localStorage.removeItem(authTokenName);
            localStorage.removeItem('dangerousFieldsEditedValues');
            localStorage.removeItem('skillsNeedDialect');
            localStorage.removeItem('onboarding');
            // Clear 2FA data from localStorage
            localStorage.removeItem('twoFactorData');
            localStorage.removeItem('challengeData');
          }
        }
      },
      logIn(state, {token, uid, rememberMe, punchOutRedirectUrl}) {
        const decoded = jwtDecode(token);
        const isGodmode = decoded.godmode;
        fetchData.setToken(token, uid);
        state.loggedIn = true;
        if (isBrowser()) {
          const authTokenName = `${process.env.NODE_ENV}_authToken`;
          if (!cookieManager.getCookie('uID')) {
            cookieManager.setCookie('uID', uid, {path: '/', expires: rememberMe ? 2592000 : ''});
          }
          if (!cookieManager.getCookie('user_id_hash')) {
            cookieManager.setCookie('user_id_hash', uid, {path: '/', expires: 2592000});
          }
          if (!isGodmode) {
            if (!cookieManager.getCookie(authTokenName)) {
              const cookieParams = {path: '/', expires: 604800};
              const domain = cookieDomain(window.location.hostname);
              if (domain) cookieParams.domain = domain;
              cookieManager.setCookie(authTokenName, token, cookieParams);
            }
          } else {
            if (cookieManager.getCookie(authTokenName)) {
              const cookieParams = {path: '/', expires: -2};
              const domain = cookieDomain(window.location.hostname);
              if (domain) cookieParams.domain = domain;
              cookieManager.setCookie(authTokenName, '', cookieParams);
            }
            cookieManager.setCookie(authTokenName, token, {path: '/', expires: 604800});
          }
          if (punchOutRedirectUrl && !cookieManager.getCookie('punch_out_redirect_url')) {
            cookieManager.setCookie('punch_out_redirect_url', punchOutRedirectUrl, {path: '/', expires: rememberMe ? 2592000 : ''});
          }
        }
      },
      setAuthInfoToStorage(state, {token, uid}) {
        if (isBrowser()) {
          localStorage.setItem('uID', uid);
          const authTokenName = `${process.env.NODE_ENV}_authToken`;
          localStorage.setItem(authTokenName, token);
        }
      },
      setResetPasswordEmail(state, value) {
        state.resetPasswordEmail = value;
      },
      setSupportMode(state) {
        state.support = true;
      },
      setGodMode(state, value) {
        state.godmode = value;
        if (isBrowser()) {
          cookieManager.setCookie('godmode', (value || ''), {path: '/', expires: 2592000});
        }
      },
      setPunchout(state, value) {
        state.punchOutRedirectUrl = value;
        if (isBrowser()) {
          cookieManager.setCookie('punch_out_redirect_url', (value || ''), {path: '/', expires: 2592000});
        }
      },
      // 2FA mutations
      setTwoFactorData(state, data) {
        state.twoFactorRequired = data.twoFactorRequired;
        state.twoFactorType = data.twoFactorType;
        state.authSessionId = data.authSessionId;
        state.authSessionExpiresAt = data.authSessionExpiresAt;

        // Store in localStorage
        if (isBrowser()) {
          localStorage.setItem('twoFactorData', JSON.stringify({
            twoFactorRequired: data.twoFactorRequired,
            twoFactorType: data.twoFactorType,
            authSessionId: data.authSessionId,
            authSessionExpiresAt: data.authSessionExpiresAt,
          }));
        }
      },
      setChallengeData(state, data) {
        // state.challengeToken = data.challengeToken;
        state.challengeExpiresAt = data.expiresAt;
        state.nextChallengeAvailableAt = data.nextChallengeAvailableAt;
        state.maskedDestination = data.maskedDestination || '';
        state.challengeSequence = data.challengeSequence;

        // Store in localStorage
        if (isBrowser()) {
          localStorage.setItem('challengeData', JSON.stringify({
            // challengeToken: data.challengeToken,
            challengeExpiresAt: data.expiresAt,
            nextChallengeAvailableAt: data.nextChallengeAvailableAt,
            maskedDestination: data.maskedDestination || '',
            challengeSequence: data.challengeSequence
          }));
        }
      },
      clearTwoFactorData(state) {
        state.twoFactorRequired = false;
        state.twoFactorType = '';
        state.authSessionId = '';
        state.authSessionExpiresAt = '';
        state.maskedDestination = '';
        state.challengeToken = '';
        state.challengeExpiresAt = '';
        state.nextChallengeAvailableAt = '';
        state.challengeSequence = '';
      }
    }
  };
};
